import React from 'react';
import {
  Box,
  Container,
  Text,
  VStack,
  HStack,
  Button,
  Heading,
  SimpleGrid,
  Icon,
  Badge,
  Progress,
  Flex,
  useColorModeValue,
  Image,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { FaPlay, FaClock, FaCheckCircle, FaBook, FaVideo, FaChartLine } from 'react-icons/fa';
import brandConfig from '../brandConfig';
import { NavbarWithCallToAction } from '../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../components/chakra/FooterWithFourColumns/FooterWithFourColumns';

interface ModuleCardProps {
  title: string;
  description: string;
  duration: string;
  icon: any;
  isCompleted?: boolean;
  isLocked?: boolean;
  onClick?: () => void;
}

const ModuleCard: React.FC<ModuleCardProps> = ({
  title,
  description,
  duration,
  icon,
  isCompleted = false,
  isLocked = false,
  onClick,
}) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = isCompleted
    ? 'green.400'
    : isLocked
    ? 'gray.300'
    : brandConfig.colors.primary;

  return (
    <Box
      bg={bgColor}
      borderRadius="lg"
      p={6}
      boxShadow="md"
      border="2px solid"
      borderColor={borderColor}
      cursor={isLocked ? 'not-allowed' : 'pointer'}
      opacity={isLocked ? 0.6 : 1}
      transition="all 0.3s"
      _hover={
        !isLocked
          ? {
              transform: 'translateY(-4px)',
              boxShadow: 'lg',
            }
          : {}
      }
      onClick={!isLocked ? onClick : undefined}
    >
      <VStack align="start" spacing={4}>
        <HStack justify="space-between" w="100%">
          <Icon as={icon} boxSize={8} color={brandConfig.colors.primary} />
          {isCompleted && (
            <Badge colorScheme="green" variant="solid">
              Completed
            </Badge>
          )}
          {isLocked && (
            <Badge colorScheme="gray" variant="solid">
              Locked
            </Badge>
          )}
        </HStack>
        <Box>
          <Heading size="md" mb={2}>
            {title}
          </Heading>
          <Text color="gray.600" fontSize="sm">
            {description}
          </Text>
        </Box>
        <HStack spacing={1} color="gray.500" fontSize="sm">
          <Icon as={FaClock} />
          <Text>{duration}</Text>
        </HStack>
      </VStack>
    </Box>
  );
};

const TrainingStart: React.FC = () => {
  const navigate = useNavigate();

  const modules = [
    {
      title: 'Welcome & Overview',
      description: 'Introduction to your surgical preparation journey',
      duration: '10 minutes',
      icon: FaBook,
      isCompleted: false,
      isLocked: false,
    },
    {
      title: 'Pre-Surgery Nutrition',
      description: 'Optimize your diet for better surgical outcomes',
      duration: '15 minutes',
      icon: FaVideo,
      isCompleted: false,
      isLocked: false,
    },
    {
      title: 'Physical Preparation',
      description: 'Exercises to strengthen your body before surgery',
      duration: '20 minutes',
      icon: FaVideo,
      isCompleted: false,
      isLocked: true,
    },
    {
      title: 'Mental Wellness',
      description: 'Manage anxiety and prepare mentally for surgery',
      duration: '15 minutes',
      icon: FaVideo,
      isCompleted: false,
      isLocked: true,
    },
    {
      title: 'Day Before Surgery',
      description: 'Final preparations and checklist',
      duration: '10 minutes',
      icon: FaBook,
      isCompleted: false,
      isLocked: true,
    },
    {
      title: 'Recovery Planning',
      description: 'Set yourself up for optimal recovery',
      duration: '15 minutes',
      icon: FaChartLine,
      isCompleted: false,
      isLocked: true,
    },
  ];

  const handleModuleClick = (index: number) => {
    if (!modules[index].isLocked) {
      navigate(`/training/module/${index + 1}`);
    }
  };

  return (
    <Flex direction="column" minH="100vh">
      <NavbarWithCallToAction />
      <Container maxW="container.xl" pt={{ base: 32, md: 40 }} pb={10} flex="1">
        <VStack spacing={8} align="stretch">
        <Box textAlign="center">
          <Heading size="xl" color={brandConfig.colors.primary} mb={4}>
            Welcome to Your Surgical Preparation Training
          </Heading>
          <Text fontSize="lg" color="gray.600" maxW="2xl" mx="auto">
            Based on your assessment, we've created a personalized training program to help you
            prepare for your upcoming surgery. Complete each module at your own pace.
          </Text>
        </Box>

        <Box
          bg={brandConfig.colors.accentLight}
          p={6}
          borderRadius="lg"
          boxShadow="md"
        >
          <VStack spacing={4}>
            <HStack justify="space-between" w="100%">
              <Text fontWeight="semibold" fontSize="lg">
                Your Progress
              </Text>
              <Text fontSize="sm" color="gray.600">
                0 of 6 modules completed
              </Text>
            </HStack>
            <Progress
              value={0}
              size="lg"
              colorScheme="blue"
              borderRadius="full"
              w="100%"
            />
          </VStack>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {modules.map((module, index) => (
            <ModuleCard
              key={index}
              {...module}
              onClick={() => handleModuleClick(index)}
            />
          ))}
        </SimpleGrid>

        <Flex justify="center" mt={8}>
          <Button
            size="lg"
            colorScheme="blue"
            leftIcon={<Icon as={FaPlay} />}
            onClick={() => handleModuleClick(0)}
          >
            Start Your First Module
          </Button>
        </Flex>

        <Box
          bg="gray.50"
          p={6}
          borderRadius="lg"
          mt={8}
        >
          <VStack spacing={4} align="start">
            <Heading size="md" color={brandConfig.colors.primary}>
              Tips for Success
            </Heading>
            <VStack align="start" spacing={2}>
              <HStack>
                <Icon as={FaCheckCircle} color="green.500" />
                <Text>Complete modules in order for the best learning experience</Text>
              </HStack>
              <HStack>
                <Icon as={FaCheckCircle} color="green.500" />
                <Text>Take notes during video modules</Text>
              </HStack>
              <HStack>
                <Icon as={FaCheckCircle} color="green.500" />
                <Text>Practice exercises daily for optimal results</Text>
              </HStack>
              <HStack>
                <Icon as={FaCheckCircle} color="green.500" />
                <Text>Reach out to support if you have any questions</Text>
              </HStack>
            </VStack>
          </VStack>
        </Box>
      </VStack>
    </Container>
    <FooterWithFourColumns />
    </Flex>
  );
};

export default TrainingStart;